import type {ReactNode, SyntheticEvent} from 'react'
import {
  faChevronUp,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {addMaybeClassName} from '@freckle/educator-entities/ts/common/helpers/classnames'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {expandButton, disabledButton} from './expand-chevron-button.module.scss'

export type ChevronDirectionT = 'up' | 'down' | 'left' | 'right'

export type ScreenReaderTextT = {
  expand: string
  collapse: string
}

type Props = {
  expand: boolean
  onClick: (e: SyntheticEvent<EventTarget, Event>) => void
  className?: string | null
  iconExpand?: ChevronDirectionT
  iconCollapse?: ChevronDirectionT
  disabled?: boolean
  screenReaderText?: ScreenReaderTextT
  ariaControls?: string
}

function ExpandChevronButton(props: Props) {
  const {
    expand,
    className,
    iconExpand = 'down',
    iconCollapse = 'up',
    disabled,
    screenReaderText,
    ariaControls,
  } = props

  const [onClick, expandButtonBaseClass] =
    disabled === true ? [() => {}, disabledButton] : [props.onClick, expandButton]

  const additionalButtonProps = {
    ...(screenReaderText !== undefined
      ? {ariaLabel: expand ? screenReaderText.expand : screenReaderText.collapse}
      : {}),
    ...(ariaControls !== undefined ? {ariaControls} : {}),
  }

  const buttonClass = addMaybeClassName(expandButtonBaseClass, className)

  return (
    <Button
      onClick={onClick}
      style="tertiary"
      className={buttonClass}
      dataTest="expand-chevron-button"
      disabled={disabled}
      ariaExpanded={expand}
      {...additionalButtonProps}
    >
      {expand ? renderIconWithDirection(iconExpand) : renderIconWithDirection(iconCollapse)}
    </Button>
  )
}

function renderIconWithDirection(direction: ChevronDirectionT): ReactNode {
  switch (direction) {
    case 'up':
      return <FontAwesomeIcon icon={faChevronUp} />
    case 'down':
      return <FontAwesomeIcon icon={faChevronDown} />
    case 'left':
      return <FontAwesomeIcon icon={faChevronLeft} />
    case 'right':
      return <FontAwesomeIcon icon={faChevronRight} />
    default:
      throw new Error(`Unrecognized chevron direction: ${direction}`)
  }
}

export {ExpandChevronButton}
